<template>
  <div
    :ref="bindRootElement"
    class="chat-event-list-item flex flex-col"
  >
    <div
      v-if="showTimestamp"
      class="chat-feed-max-w mx-auto flex w-full justify-center px-2 phone:px-2"
    >
      <chat-event-timestamp
        :timestamp="item.timestamp"
      />
    </div>
    <transition name="chat-unread-banner">
      <chat-unread-messages-banner
        v-if="showUnreadBanner"
      />
    </transition>
    <div class="chat-feed-max-w mx-auto flex w-full flex-col px-2 phone:px-2">
      <div
        v-if="(item.item.type === 'IncomingMessageEvent') && (item.item as MessageIncomingEvent).executor !== currentUser?.imLogin"
        class="px-5 phone:px-0"
        :class="isGroupedWithPrevious ? 'pt-1' : 'pt-3'"
      >
        <div
          :class="{ 'bg-comp-messages-selection-bg-pres -my-0.5 py-0.5': isMessageUnderlayActive }"
        >
          <chat-event-incoming
            data-test="chat-event-incoming"
            :data-event-id="item.eventId"
            :event="(item as HistoryMessageItem<MessageIncomingEvent>)"
            :next="(next as HistoryMessageItem<MessageIncomingEvent>)"
            :following="(following as HistoryMessageItem<MessageIncomingEvent>)"
            :quoted-event="quotedIncomingEvent"
            :light="!!light"
            :is-edited="!!(item.item as MessageIncomingEvent).era"
            :is-message-deleted="isEventDeleted"
            :render-heavy="renderHeavy"
            class="duration-300 ease-in"
            @click="onChatEventClick($event)"
            @click-event-avatar="onAvatarClick($event)"
            @scroll-to-item="(targetEventId, sourceEventId) => emit('scrollToItem', targetEventId, sourceEventId)"
            @dblclick="emit('on-message-dbl-click', item.eventId)"
            @click.right="emit('onRightClick', $event)"
            @reply="emit('reply')"
            @resize="emit('resize')"
          />
        </div>
      </div>

      <div
        v-else-if="item.item.type === 'OutgoingMessageEvent' || (item.item.type === 'IncomingMessageEvent' && (item.item as MessageIncomingEvent).executor === currentUser?.imLogin)"
        class="px-5 phone:px-0"
        :class="isGroupedWithPrevious ? 'pt-1' : 'pt-3'"
      >
        <div
          :class="{ 'bg-comp-messages-selection-bg-pres -my-0.5 py-0.5': isMessageUnderlayActive }"
        >
          <chat-event-outgoing
            data-test="chat-event-outgoing"
            :event="(item as HistoryMessageItem<MessageOutgoingEvent>)"
            :quoted-event="quotedOutgoingEvent"
            :next="(next as HistoryMessageItem<MessageOutgoingEvent>)"
            :data-event-id="item.eventId"
            :light="!!light"
            :is-edited="!!(item.item as MessageOutgoingEvent).era"
            :is-message-deleted="isEventDeleted"
            :render-heavy="renderHeavy"
            class="duration-300 ease-in"
            @click="onChatEventClick($event)"
            @scroll-to-item="(targetEventId, sourceEventId) => emit('scrollToItem', targetEventId, sourceEventId)"
            @dblclick="emit('on-message-dbl-click', item.eventId)"
            @click.right="emit('onRightClick', $event)"
            @resize="emit('resize')"
          />
        </div>
      </div>

      <call-chat-event
        v-else-if="item.item.type === 'MessageCallReceived'"
        :light="!!light"
        :event="(item as HistoryMessageItem<MessageCallReceived>)"
        :next-chat-event="next!"
        class="px-5 phone:px-2"
        @appointment-event-click="onAppointmentClick"
      />

      <chat-event-system-message
        v-else-if="isConferenceEditMessage(item)"
        :event="item"
      />

      <chat-pinned-event-item
        v-else-if="isPinnedMessage(item)"
        :event="item"
        @scroll-to-item="(targetEventId) => emit('scrollToItem', targetEventId)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { UnlistenFn } from '@tauri-apps/api/event'
import {
  isConferenceEditMessage,
  isPinnedMessage,
  HistoryMessageItem,
  MessageCallReceived,
  MessageIncomingEvent,
  MessageOutgoingEvent,
  isMessageDeleted,
} from '@wui/im'
import { unix } from 'dayjs'
import {
  computed,
  ref,
  toRefs,
  onUpdated,
  onMounted,
  onBeforeUnmount,
  watch,
  onUnmounted,
  type ComponentPublicInstance,
} from 'vue'

import { ChatEventSystemMessage } from '@/widgets/chat-event-system-message'
import { ChatPinnedEventItem } from '@/widgets/chat-pinned-event-item'

import { CallChatEvent } from '@/entities/call-chat-event'
import { ChatEventIncoming } from '@/entities/chat-event-incoming'
import { ChatEventOutgoing } from '@/entities/chat-event-outgoing'
import { ChatEventTimestamp } from '@/entities/chat-event-timestamp'
import {
  isSameMessageAuthor,
  markChatEventHeavyRendered,
  useChatEvent,
  wasChatEventHeavyRendered,
} from '@/entities/chat-handlers'
import { ChatUnreadMessagesBanner } from '@/entities/chat-unread-messages-banner'
import { useStoreContacts } from '@/entities/contact'
import { chatModel } from '@/entities/messages'
import { useNotInHistoryMessagesStore, loadMessage } from '@/entities/not-in-history-messages'

import { useCurrentUser } from '@/shared/composables/current-user'
import { isMentionAllUserId } from '@/shared/services/mention-helper'
import { isWindowHidden, onWindowVisibilityChange } from '@/shared/services/window-visibility'
import { optionalPush } from '@/shared/utils/router'
import { getChatRoute } from '@/shared/utils/url'

const props = defineProps<{
  item: HistoryMessageItem
  next?: HistoryMessageItem
  following?: HistoryMessageItem
  light?: boolean
  pServed: string
  activeChatEvent?: HistoryMessageItem | null
  selected?: boolean
  isReadingLocked?: boolean,
  hideQuote?: boolean
  unreadBannerEventId?: number | undefined
}>()

const emit = defineEmits<{
  (event: 'scrollToItem', targetEventId: number, sourceEventId?: number): void
  (event: 'appointment-event-click', value: {
    e: MouseEvent | TouchEvent,
    message: string
  }): void,
  (event: 'visible', value: HistoryMessageItem): void
  (event: 'on-message-dbl-click', value: number): void
  (event: 'onRightClick', value: MouseEvent | TouchEvent): void
  (event: 'reply'): void
  (event: 'resize'): void
}>()

const { findById } = chatModel.useChatMessageStore()
const { findByPServed } = useStoreContacts()
const { currentUser } = useCurrentUser()

const { getMessageByEventId, addMessage } = useNotInHistoryMessagesStore()

const { item, next, isReadingLocked } = toRefs(props)

const isEventDeleted = computed(() => {
  return isMessageDeleted(item.value as HistoryMessageItem<MessageOutgoingEvent | MessageIncomingEvent>)
})

const showTimestamp = computed(() => {
  if (!item.value?.timestamp) return true
  if (!next?.value?.timestamp) return true

  const event1 = unix(item.value.timestamp).tz()
  const event2 = unix(next.value.timestamp).tz()

  return !event1.isSame(event2, 'day')
})

const showUnreadBanner = computed(() =>
  Boolean(props.unreadBannerEventId && item.value?.eventId === props.unreadBannerEventId),
)

const onChatEventClick = (event: PointerEvent) => {
  const target = event.target
  if (!target) return
  const htmlElement = (target as HTMLElement)
  const contactUri = htmlElement.getAttribute('data-contact-uri')
  if (contactUri && contactUri !== currentUser.value?.imLogin && !isMentionAllUserId(contactUri)) {
    optionalPush(getChatRoute(contactUri))
  }
}

const onAvatarClick = (event: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>) => {
  if (event.item.executor && findByPServed(event.item.executor)) {
    optionalPush(getChatRoute(event.item.executor))
  }
}

const getQuotedEventId = () => {
  return (item.value.item as MessageIncomingEvent | MessageOutgoingEvent).quote?.quotedEventId
}

const isRenderableQuote = (event: HistoryMessageItem) => {
  const quoteItem = event.item as MessageIncomingEvent
  return Boolean(
    quoteItem.messageText
    || quoteItem.files?.length
    || quoteItem.forwarding?.forwardingEventId,
  )
}

const quotedEventFallback = ref<HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>>()
let quoteLoadId = 0
let isDisposed = false

/** Defer files/quotes/reactions until near viewport (faster virtua first measure). */
const renderHeavy = ref(wasChatEventHeavyRendered(item.value?.eventId))
/** Prefetch ahead of the viewport so images start loading before the bubble is on screen. */
const HEAVY_ROOT_MARGIN = '1000px 0px'
let heavyObserver: IntersectionObserver | undefined

const quotedEvent = computed((): HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent> | undefined => {
  if (!renderHeavy.value || props.hideQuote) {
    return undefined
  }
  const quotedEventId = getQuotedEventId()
  if (!quotedEventId) {
    return undefined
  }
  const event = findById(quotedEventId) ?? getMessageByEventId(quotedEventId) ?? quotedEventFallback.value
  if (!event || !isRenderableQuote(event)) {
    return undefined
  }
  return event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
})

const quotedIncomingEvent = computed((): HistoryMessageItem<MessageIncomingEvent> | undefined => (
  quotedEvent.value as HistoryMessageItem<MessageIncomingEvent> | undefined
))

const quotedOutgoingEvent = computed((): HistoryMessageItem<MessageOutgoingEvent> | undefined => (
  quotedEvent.value as HistoryMessageItem<MessageOutgoingEvent> | undefined
))

const resolveQuotedEvent = async () => {
  const requestId = ++quoteLoadId
  quotedEventFallback.value = undefined
  if (!renderHeavy.value || props.hideQuote) {
    return
  }
  const quotedEventId = getQuotedEventId()
  if (!quotedEventId || findById(quotedEventId) || getMessageByEventId(quotedEventId)) {
    return
  }

  const event = await loadMessage(props.pServed, quotedEventId)
  if (isDisposed || requestId !== quoteLoadId || !event) {
    return
  }

  if (event.eventId) {
    addMessage(event.eventId, (event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>))
  }
  if (isRenderableQuote(event)) {
    quotedEventFallback.value = event as HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>
  }
}

const onVisible = () => {
  emit('visible', item.value)
}

const { el, observer } = useChatEvent(item, next?.value ? next : ref(), onVisible, false, isReadingLocked)
const isGroupedWithPrevious = computed(() => isSameMessageAuthor(item.value, next?.value))

const isMessageUnderlayActive = computed(() => (
  props.activeChatEvent === item.value || Boolean(props.selected) || Boolean(props.light)
))

const enableHeavyRender = () => {
  if (renderHeavy.value) {
    return
  }
  renderHeavy.value = true
  markChatEventHeavyRendered(item.value?.eventId)
  heavyObserver?.disconnect()
  heavyObserver = undefined
  emit('resize')
}

const observeHeavyProximity = () => {
  if (renderHeavy.value) {
    return
  }
  if (typeof IntersectionObserver === 'undefined') {
    enableHeavyRender()
    return
  }
  if (!el.value) {
    return
  }
  if (!heavyObserver) {
    heavyObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          enableHeavyRender()
        }
      },
      { rootMargin: HEAVY_ROOT_MARGIN, threshold: 0 },
    )
  }
  heavyObserver.observe(el.value)
}

const bindRootElement = (node: Element | ComponentPublicInstance | null) => {
  if (!(node instanceof Element)) {
    el.value = undefined
    return
  }
  el.value = node as HTMLDivElement
  observeHeavyProximity()
}

watch(
  () => [
    renderHeavy.value,
    props.hideQuote,
    (item.value.item as MessageIncomingEvent | MessageOutgoingEvent).quote?.quotedEventId,
  ] as const,
  () => {
    if (!renderHeavy.value) {
      return
    }
    void resolveQuotedEvent()
  },
  { immediate: true },
)

const resetObservable = () => {
  if (el.value) {
    observer.unobserve(el.value)
    observer.observe(el.value)
  }
}

const onAppointmentClick = (payload: {
    e: MouseEvent | TouchEvent,
    message: string
}) => {
  emit('appointment-event-click', payload)
}

const focused = ref(true)

isWindowHidden().then((hidden) => {
  if (isDisposed) {
    return
  }
  focused.value = !hidden
})

onUpdated(() => {
  if (focused.value) {
    resetObservable()
  }
})

onMounted(() => {
  if (focused.value) {
    resetObservable()
  }
  observeHeavyProximity()
})

let unListenVisibility: UnlistenFn | undefined

onWindowVisibilityChange((event: boolean) => {
  if (isDisposed) {
    return
  }
  focused.value = event
  if (event) {
    resetObservable()
  }
}).then((unlisten) => {
  if (isDisposed) {
    unlisten()
    return
  }
  unListenVisibility = unlisten
})

onBeforeUnmount(() => {
  isDisposed = true
  quoteLoadId++
  heavyObserver?.disconnect()
  heavyObserver = undefined
  observer.disconnect()
  el.value = undefined
})

onUnmounted(() => {
  unListenVisibility?.()
})
</script>
<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.chat-unread-banner-leave-active {
  transition: opacity 0.45s ease;
}

.chat-unread-banner-leave-to {
  opacity: 0;
}
</style>
